// /frontend/src/components/Sidebar.jsx

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logout as apiLogout } from "../auth/Auth";
import {
  LayoutDashboard,
  Users,
  ListChecks,
  Settings,
  Smile,
  LogIn,
  UserPlus,
  CalendarDays, // AEP Wizard icon
  Shield,       // OEP Hub icon
  BarChart3,    // Founder Dashboard icon
  Calendar,     // Calendar page icon
  ClipboardList, // Audit Logs icon
  UserCog,      // User Management icon
  AlertTriangle, // Risk Management icon
  Zap,          // Automations icon
  PhoneCall,    // Service Dashboard icon
  TrendingUp,   // Retention Dashboard icon
  FileText,     // Enrollment Tracking icon
  Building2,    // Agency Dashboard icon
  MessageSquare, // Conversations icon
  DollarSign,   // Commissions icon
  Activity,     // Daily Recap icon
  Sunrise,      // Morning Brief icon
  Briefcase,    // Book of Business icon
  Target,       // Prospecting icon
} from "lucide-react";
import { PendingApprovalsBadge } from "./PendingApprovals";
import { useBranding } from '../context/BrandingContext';

const CommissionsNewBadge = () => (
  <span className="ml-auto flex items-center">
    <span className="relative flex h-5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-40"></span>
      <span className="relative inline-flex items-center rounded-full bg-green-500 px-2 py-0.5 text-[10px] font-bold text-white">
        New!
      </span>
    </span>
  </span>
);

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { logoUrl, primaryColor } = useBranding();
  const [userRole, setUserRole] = useState(null);
  const [showNewBadge, setShowNewBadge] = useState(() => {
    return localStorage.getItem('commissions-new-dismissed') !== 'true';
  });

  // Fetch user role on mount
  useEffect(() => {
    async function fetchUserRole() {
      try {
        const BASE = process.env.REACT_APP_API_URL || "";
        const res = await fetch(`${BASE}/auth/me`, {
          credentials: 'include' // Use httpOnly cookie
        });
        const json = await res.json();
        console.log('[Sidebar] /auth/me response:', json);
        if (res.ok && json.ok) {
          setUserRole(json.user?.role);
          console.log('[Sidebar] User role set to:', json.user?.role);
        } else {
          console.warn('[Sidebar] /auth/me failed:', json.error);
        }
      } catch (err) {
        console.error("[Sidebar] Failed to fetch user role:", err);
      }
    }
    fetchUserRole();
  }, []);

  // Days remaining until next Oct 15 (AEP start)
  const daysUntilAEP = (() => {
    const now = new Date();
    const year =
      now.getMonth() > 9 || (now.getMonth() === 9 && now.getDate() > 15)
        ? now.getFullYear() + 1
        : now.getFullYear();
    // Month is 0-indexed; 9 = October
    const target = new Date(year, 9, 15, 0, 0, 0, 0);
    const diffMs = target.getTime() - now.getTime();
    const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    return days;
  })();

  // OEP season active? (Jan–Mar)
  const oepBadge = (() => {
    const now = new Date();
    const inSeason = now.getMonth() <= 2; // Jan=0..Mar=2
    return inSeason ? "OEP" : undefined;
  })();

  const dashboardMode = [
    "/dashboard",
    "/founder-dashboard",
    "/founder",
    "/clients",
    "/tasks",
    "/calendar",
    "/settings",
    "/automations",
    "/aep-wizard",
    "/oep",
    "/risk",
    "/service",
    "/communications",
    "/conversations",
    "/commissions",
    "/audit-logs",
    "/admin",
    "/agency",
    "/phone-blast",
    "/recap",
    "/daily-brief",
    "/fmo",
    "/retention",
    "/enrollment-tracking",
    "/prospecting",
  ].some((path) => location.pathname.startsWith(path));

  // Check if user is an agency role (owner or admin)
  const isAgencyRole = userRole && ["agency", "agency_owner", "agency_admin"].includes(userRole);

  // Check if user is FMO role
  const isFmoRole = userRole === 'fmo';

  // Check if user is founder/fmo level (sees admin features)
  const isFounderLevel = userRole && ["fmo", "founder"].includes(userRole);

  // Build dashboard links based on role
  const dashboardLinks = [
    // FMO role sees FMO-specific navigation
    ...(isFmoRole
      ? [
          { to: "/fmo/dashboard", label: 'FMO Dashboard', icon: <Building2 size={20} /> },
          { to: "/fmo/agencies", label: 'Agencies', icon: <Building2 size={20} /> },
          { to: "/fmo/agents", label: 'Agent Directory', icon: <Users size={20} /> },
          { to: "/fmo/analytics", label: 'Retention Analytics', icon: <TrendingUp size={20} /> },
          { to: "/fmo/risk", label: 'Risk Overview', icon: <AlertTriangle size={20} /> },
          { to: "/fmo/revenue", label: 'Revenue', icon: <DollarSign size={20} /> },
          { to: "/fmo/compliance", label: 'Compliance', icon: <Shield size={20} /> },
          { to: "/fmo/enrollment", label: 'Enrollment Periods', icon: <FileText size={20} /> },
          { to: "/fmo/reports", label: 'Reports', icon: <BarChart3 size={20} /> },
          { to: "/fmo/settings", label: 'Settings', icon: <Settings size={20} /> },
        ]
      : isAgencyRole
      ? [
          { to: "/agency/dashboard", label: 'Agency Dashboard', icon: <Building2 size={20} /> },
          { to: "/daily-brief", label: 'Daily Brief', icon: <Sunrise size={20} /> },
          { to: "/agency/risk", label: 'Risk Center', icon: <AlertTriangle size={20} /> },
          { to: "/agency/agents", label: 'Agent Management', icon: <UserCog size={20} /> },
          { to: "/agency/clients", label: 'All Clients', icon: <Users size={20} /> },
          { to: "/agency/retention", label: 'Retention Center', icon: <TrendingUp size={20} /> },
          { to: "/agency/leaderboard", label: 'Leaderboard', icon: <BarChart3 size={20} /> },
          { to: "/agency/compliance", label: 'Compliance', icon: <Shield size={20} /> },
          { to: "/agency/commissions", label: 'Commissions', icon: <DollarSign size={20} /> },
          { to: "/agency/campaigns", label: 'Campaigns', icon: <MessageSquare size={20} /> },
          { to: "/agency/enrollment-tracker", label: 'Enrollment Tracker', icon: <FileText size={20} /> },
          { to: "/agency/book-of-business", label: 'Book of Business', icon: <Briefcase size={20} /> },
          { to: "/agency/reports", label: 'Reports', icon: <BarChart3 size={20} /> },
          { to: "/agency/recruiting", label: 'Recruiting Pipeline', icon: <UserPlus size={20} /> },
          { to: "/service", label: 'Service Center', icon: <PhoneCall size={20} /> },
          { to: "/settings", label: t('settings'), icon: <Settings size={20} /> },
          { to: "/agency/setup", label: 'Agency Setup', icon: <Settings size={20} /> },
        ]
      : [
          // Standard agent dashboard
          { to: "/dashboard",          label: t('dashboard'),     icon: <LayoutDashboard size={20} /> },
          { to: "/daily-brief",        label: 'Daily Brief',      icon: <Sunrise size={20} /> },
          { to: "/communications",     label: 'Communications',   icon: <MessageSquare size={20} /> },
          { to: "/tasks",              label: t('tasks'),         icon: <ListChecks size={20} /> },
          { to: "/calendar",           label: t('calendar'),      icon: <Calendar size={20} /> },
          { to: "/clients",            label: t('clients'),       icon: <Users size={20} /> },
          { to: "/risk",               label: 'Risk Management',  icon: <AlertTriangle size={20} /> },
          { to: "/enrollment-tracking",label: 'Enrollments',      icon: <FileText size={20} />, comingSoon: true },
          { to: "/prospecting",        label: 'Prospecting',      icon: <Target size={20} />,  comingSoon: true },
          { to: "/aep-wizard",         label: t('aepWizard'),     icon: <CalendarDays size={20} /> },
          { to: "/oep",                label: t('oepHub'),        icon: <Shield size={20} />, badge: oepBadge },
          { to: "/commissions",        label: 'Commissions',      icon: <DollarSign size={20} />,
            ...(showNewBadge ? { badgeComponent: <CommissionsNewBadge /> } : {})
          },
          { to: "/service",            label: 'Service Center',   icon: <PhoneCall size={20} /> },
          { to: "/automations",        label: 'Automations',      icon: <Zap size={20} />, badgeComponent: <PendingApprovalsBadge /> },
          { to: "/settings",           label: t('settings'),      icon: <Settings size={20} /> },
        ]
    ),
    // Founder level features (admin tools)
    ...(isFounderLevel
      ? [
          { to: "/founder", label: t('founderMetrics') || 'Founder Metrics', icon: <BarChart3 size={20} /> },
          { to: "/audit-logs", label: t('auditLogs') || 'Audit Logs', icon: <ClipboardList size={20} /> },
          { to: "/admin/users", label: 'User Management', icon: <UserCog size={20} /> },
        ]
      : []
    ),
  ];

  const authLinks = [
    { to: "/",       label: t('login'),   icon: <LogIn size={20} /> },
    { to: "/signup", label: t('signUp'), icon: <UserPlus size={20} /> },
  ];

  const links = dashboardMode ? dashboardLinks : authLinks;

  return (
    <aside
      className="w-64 min-h-full flex flex-col font-[Inter] shadow-2xl"
      style={{ background: "#172A3A" }}
    >
      <Link
        to={isAgencyRole ? "/agency/dashboard" : "/dashboard"}
        className="flex flex-col items-center gap-2 px-6 py-5 border-b flex-shrink-0"
        style={{ borderColor: "#20344A" }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-full shadow object-contain bg-white" style={{ objectFit: 'contain' }} />
        ) : (
          <img src="/logo.png" alt="Pro IRP Logo" className="w-12 h-12 rounded-full shadow bg-white" style={{ objectFit: "contain" }} />
        )}
        <span className="text-xl font-extrabold tracking-tight" style={{ color: primaryColor || "#FFB800", letterSpacing: "-2px" }}>
          Pro <span className="text-white">IRP</span>
        </span>
      </Link>

      <nav className="flex-1 flex flex-col py-4 gap-2 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-gray-600">
        <ul className="space-y-1">
          {links.map(({ to, label, icon, badge, badgeComponent, comingSoon }) => {
            const isActive =
              location.pathname === to || location.pathname.startsWith(`${to}/`);

            if (comingSoon) {
              return (
                <li key={to}>
                  <span className="flex items-center gap-3 px-6 py-3 rounded-xl text-lg font-semibold opacity-50 cursor-not-allowed text-white">
                    {icon}
                    {label}
                    <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold bg-gray-500 text-white whitespace-nowrap">
                      Coming Soon
                    </span>
                  </span>
                </li>
              );
            }

            return (
              <li key={to}>
                <Link
                  to={to}
                  data-tour={`sidebar-${to.replace(/^\//, '').replace(/\//g, '-') || 'home'}`}
                  onClick={() => {
                    if (to === '/commissions' && showNewBadge) {
                      setShowNewBadge(false);
                      localStorage.setItem('commissions-new-dismissed', 'true');
                    }
                  }}
                  className={`flex items-center gap-3 px-6 py-3 rounded-xl text-lg font-semibold transition
                    ${
                      isActive
                        ? "bg-[#20344A] text-[#FFB800]"
                        : "text-white hover:bg-[#20344A] hover:text-[#FFB800]"
                    }
                  `}
                >
                  {icon}
                  {label}
                  {badgeComponent && (
                    <span className="ml-auto">{badgeComponent}</span>
                  )}
                  {badge && (
                    <span
                      className={`ml-auto rounded-full px-2 py-0.5 text-xs font-bold
                        ${
                          isActive
                            ? "bg-white text-[#172A3A]"
                            : "bg-[#FFB800] text-[#172A3A]"
                        }
                      `}
                      title={
                        label === t('aepWizard')
                          ? t('daysUntilAEP')
                          : t('oepSeason')
                      }
                    >
                      {badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {dashboardMode && (
        <div className="py-4 px-6 flex-shrink-0">
          <button
            onClick={async () => {
              await apiLogout(); // Clear httpOnly cookie via backend
              window.location.href = '/login';
            }}
            className="flex items-center gap-2 w-full py-2 px-3 rounded-xl transition font-bold shadow hover:opacity-90"
            style={{ background: "#FFB800", color: "#172A3A" }}
          >
            {t('logOut')}
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
